import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ScrollText, ChevronDown, ChevronUp, ExternalLink, Stamp, Shield,
  Building2, FileText, Fingerprint, HeartPulse, Syringe, Landmark, Crosshair, Flame
} from 'lucide-react';

const EXECUTIVE_ORDERS = [
  {
    id: 'eo-13848',
    priorityRank: 1,
    number: '13848',
    date: '12 septembre 2018',
    signatory: 'Donald J. Trump',
    shortTitleFr: "Sanctions en cas d'Ingérence Étrangère dans les Élections Américaines",
    originalTitle: 'Executive Order 13848 — Imposing Certain Sanctions in the Event of Foreign Interference in a United States Election',
    legalBasis: 'IEEPA · NEA · INA §212(f) · art. 301 titre 3 USC',
    federalRegister: '83 FR 46843',
    status: 'En vigueur (prolongé annuellement)',
    badgeLabel: 'INGÉRENCE ÉLECTORALE',
    badgeClass: 'eo-badge--violet',
    icon: Fingerprint,
    resume: (
      <>
        <p>Le Président Trump déclare l'urgence nationale face à la menace d'ingérence étrangère dans les élections américaines. Ce décret établit un mécanisme automatique et contraignant :</p>
        <ol>
          <li><strong className="text-amber-300">Évaluation obligatoire</strong> — Dans les 45 jours suivant chaque élection, le Directeur du Renseignement National (DNI) doit produire une évaluation complète de toute ingérence étrangère, identifiant les méthodes, les personnes impliquées et les gouvernements commanditaires.</li>
          <li><strong className="text-amber-300">Rapport d'impact</strong> — Le Procureur Général et le Secrétaire à la Sécurité Intérieure évaluent ensuite si l'ingérence a affecté l'intégrité des infrastructures électorales, la tabulation des votes ou la transmission des résultats.</li>
          <li><strong className="text-amber-300">Sanctions automatiques</strong> — Le Secrétaire au Trésor gèle tous les avoirs des personnes étrangères impliquées dans l'ingérence électorale. Les sanctions incluent :
            <ul>
              <li>Gel total des propriétés et intérêts financiers</li>
              <li>Restrictions d'exportation</li>
              <li>Interdiction de prêts bancaires</li>
              <li>Restrictions sur les transactions de change</li>
              <li>Interdiction d'investissement par des citoyens américains</li>
              <li>Exclusion des dirigeants du territoire américain</li>
            </ul>
          </li>
          <li><strong className="text-amber-300">Sanctions sectorielles</strong> — Possibilité de sanctionner les plus grandes entreprises des pays commanditaires dans les secteurs : services financiers, défense, énergie, technologie et transports.</li>
        </ol>
      </>
    ),
    strategicNote: `Ce décret est la pierre angulaire juridique qui permet de frapper au portefeuille TOUS les acteurs étrangers ayant interféré dans les élections américaines. Il vise directement les réseaux de manipulation médiatique, la propagande cachée, et les opérations cybernétiques menées par des puissances étrangères (Chine/PCC, cartels d'influence). C'est l'outil par lequel les sanctions massives contre le système de contrôle mondial peuvent être déclenchées LÉGALEMENT après chaque cycle électoral. L'évaluation du DNI post-2020 et post-2024 active ce mécanisme à pleine puissance.`,
  },
  {
    id: 'eo-13818',
    priorityRank: 2,
    number: '13818',
    date: '20 décembre 2017',
    signatory: 'Donald J. Trump',
    shortTitleFr: "Gel des Avoirs — Droits de l'Homme & Corruption (Global Magnitsky)",
    originalTitle: 'Executive Order 13818 — Blocking the Property of Persons Involved in Serious Human Rights Abuse or Corruption',
    legalBasis: 'IEEPA · NEA · Global Magnitsky Act (P.L. 114-328) · INA §212(f)',
    federalRegister: '82 FR 60839',
    status: 'En vigueur',
    badgeLabel: "DROITS DE L'HOMME & ANTI-CORRUPTION",
    badgeClass: 'eo-badge--amber',
    icon: Shield,
    resume: (
      <>
        <p>Le Président Trump déclare que la gravité et l'étendue des violations des droits de l'homme et de la corruption à travers le monde constituent une menace extraordinaire pour la sécurité nationale, la politique étrangère et l'économie des États-Unis. Il déclare l'urgence nationale.</p>
        <p className="mt-3"><strong className="text-amber-300">Mécanismes principaux :</strong></p>
        <ol>
          <li><strong>Gel total des avoirs</strong> pour toute personne étrangère :
            <ul>
              <li>Responsable ou complice de violations graves des droits de l'homme</li>
              <li>Officiel gouvernemental (actuel ou ancien) impliqué dans la corruption, le détournement d'actifs publics, l'expropriation à des fins personnelles, la corruption liée aux contrats gouvernementaux ou à l'extraction de ressources naturelles</li>
              <li>Dirigeant d'entité ayant participé à ces activités</li>
              <li>Ayant fourni un soutien financier, matériel ou technologique à ces activités</li>
            </ul>
          </li>
          <li><strong>Interdiction d'entrée</strong> sur le territoire américain pour ces personnes et leurs proches.</li>
          <li><strong>Extension aux complices</strong> — Toute personne ayant matériellement assisté, sponsorisé ou fourni un support aux personnes sanctionnées est elle-même sanctionnée.</li>
          <li><strong>Annexe initiale</strong> — Le décret inclut une liste initiale de personnes sanctionnées dès sa signature.</li>
        </ol>
      </>
    ),
    strategicNote: `C'est le Magnitsky Act présidentiel, l'arme nucléaire juridique contre la corruption mondiale. Ce décret permet au Président de geler les avoirs de N'IMPORTE QUEL individu sur la planète impliqué dans la corruption ou les violations des droits de l'homme — politiciens, oligarques, trafiquants, pédocriminels. Depuis sa signature, des MILLIERS d'individus et d'entités ont été ajoutés à la liste OFAC. C'est l'outil par lequel les fortunes des élites corrompues sont saisies, leurs comptes gelés, leurs voyages interdits. Le « Grand Nettoyage » s'appuie massivement sur ce décret.`,
  },
  {
    id: 'eo-13813',
    priorityRank: 3,
    number: '13813',
    date: '12 octobre 2017',
    signatory: 'Donald J. Trump',
    shortTitleFr: 'Promouvoir le Choix et la Concurrence en matière de Soins de Santé',
    originalTitle: 'Executive Order 13813 — Promoting Healthcare Choice and Competition Across the United States',
    legalBasis: 'Constitution · ERISA · PPACA (Affordable Care Act)',
    federalRegister: '82 FR 48385',
    status: 'Partiellement révoqué en 2021',
    badgeLabel: 'SANTÉ · CONCURRENCE',
    badgeClass: 'eo-badge--red',
    icon: HeartPulse,
    resume: (
      <>
        <p>Ce décret vise à démanteler le monopole de l'assurance santé imposé par l'Obamacare (PPACA) qui a doublé les primes d'assurance et éliminé la concurrence dans un tiers des comtés américains.</p>
        <p className="mt-3"><strong className="text-amber-300">Actions ordonnées :</strong></p>
        <ol>
          <li><strong>Plans de Santé Associatifs (AHP)</strong> — Permettre aux petites entreprises de se regrouper pour obtenir les mêmes conditions que les grandes corporations, contournant les mandats coûteux de l'Obamacare.</li>
          <li><strong>Assurance de courte durée (STLDI)</strong> — Réautoriser les polices de moins de 12 mois comme alternative abordable aux échanges gouvernementaux, après que l'administration Obama les avait limitées à 3 mois.</li>
          <li><strong>Comptes de Remboursement Santé (HRA)</strong> — Donner aux employeurs plus de flexibilité pour financer directement les soins de leurs employés.</li>
          <li><strong>Rapports au Président</strong> tous les 2 ans identifiant les lois et régulations qui entravent la concurrence.</li>
        </ol>
      </>
    ),
    strategicNote: `Ce décret est la première salve contre le cartel pharmaceutique et assurantiel qui a utilisé l'Obamacare pour créer un monopole captif sur la santé des Américains. En brisant ce monopole, Trump attaque directement le modèle économique de Big Pharma et des assureurs qui financent le système politique corrompu. C'est la base de la libération sanitaire du peuple américain, préfigurant la fin du contrôle médical exercé pendant le COVID.`,
  },
  {
    id: 'eo-13911',
    priorityRank: 4,
    number: '13911',
    date: '27 mars 2020',
    signatory: 'Donald J. Trump',
    shortTitleFr: "Délégation d'Autorité — Defense Production Act (COVID-19)",
    originalTitle: 'Executive Order 13911 — Delegating Additional Authority Under the Defense Production Act With Respect to Health and Medical Resources To Respond to the Spread of COVID-19',
    legalBasis: 'Defense Production Act of 1950 (50 U.S.C. 4501)',
    federalRegister: '85 FR 18403',
    status: 'En vigueur (post-pandémie)',
    badgeLabel: 'DÉFENSE · PRODUCTION MÉDICALE',
    badgeClass: 'eo-badge--green',
    icon: Syringe,
    resume: (
      <>
        <p>Face à la pandémie de COVID-19, le Président Trump active le <strong className="text-amber-300">Defense Production Act</strong> (loi de production de défense, datant de 1950) pour mobiliser l'appareil industriel américain :</p>
        <ol>
          <li><strong>Production d'urgence</strong> — Ordonne l'expansion de la production nationale d'équipements de protection individuelle (EPI) et de ventilateurs.</li>
          <li><strong>Délégation au HHS et DHS</strong> — Le Secrétaire à la Santé et le Secrétaire à la Sécurité Intérieure reçoivent l'autorité présidentielle pour :
            <ul>
              <li>Garantir des prêts aux fabricants</li>
              <li>Commander directement des achats</li>
              <li>Restaurer les capacités industrielles nationales</li>
              <li>Contrôler la distribution des ressources médicales</li>
            </ul>
          </li>
          <li><strong>Accords volontaires du secteur privé</strong> — Autorise la coopération entre entreprises privées pour la production et la distribution, levant les restrictions antitrust.</li>
          <li><strong>Nomination d'un Coordinateur</strong> — L'Assistant au Président pour la Politique Commerciale et Manufacturière sert de coordinateur national du DPA.</li>
        </ol>
      </>
    ),
    strategicNote: `En activant le Defense Production Act, Trump prend le contrôle direct de la chaîne d'approvisionnement médicale, brisant la dépendance envers la Chine (PCC) pour les équipements médicaux critiques. Ce décret est l'outil qui a permis de rapatrier la production stratégique sur sol américain et de documenter les défaillances volontaires de la chaîne d'approvisionnement mondiale. Il expose comment des acteurs étrangers avaient volontairement rendu l'Amérique dépendante pour ses ressources médicales les plus essentielles — une arme biologique économique.`,
  },
  {
    id: 'eo-13873',
    priorityRank: 5,
    number: '13873',
    date: '15 mai 2019',
    signatory: 'Donald J. Trump',
    shortTitleFr: "Sécurisation de la Chaîne d'Approvisionnement TIC",
    originalTitle: 'Executive Order 13873 — Securing the Information and Communications Technology and Services Supply Chain',
    legalBasis: 'IEEPA · NEA · art. 301 titre 3 USC',
    federalRegister: '84 FR 22689',
    status: 'En vigueur',
    badgeLabel: "CYBERSÉCURITÉ & CHAÎNE D'APPROVISIONNEMENT",
    badgeClass: 'eo-badge--blue',
    icon: Shield,
    resume: (
      <>
        <p>Le Président déclare l'urgence nationale face à la menace que représentent les adversaires étrangers exploitant les vulnérabilités des technologies de l'information et des communications (TIC).</p>
        <p className="mt-3"><strong className="text-amber-300">Mesures principales :</strong></p>
        <ol>
          <li><strong>Interdiction totale</strong> de toute acquisition, importation, transfert, installation ou utilisation de technologies et services TIC provenant de personnes contrôlées par un adversaire étranger, lorsque cela :
            <ul>
              <li>Pose un risque de sabotage ou subversion des infrastructures critiques</li>
              <li>Menace la résilience de l'économie numérique américaine</li>
              <li>Constitue un risque inacceptable pour la sécurité nationale</li>
            </ul>
          </li>
          <li><strong>Pouvoir de blocage</strong> — Le Secrétaire au Commerce peut identifier et bloquer toute transaction impliquant des technologies d'adversaires étrangers.</li>
          <li><strong>Définitions clés</strong> — Les « adversaires étrangers » sont définis comme tout gouvernement ou entité non-gouvernementale engagé dans un pattern de conduite significativement adverse à la sécurité nationale américaine.</li>
          <li><strong>Règlements d'application</strong> — Le Commerce doit publier des règles dans les 150 jours identifiant les pays et personnes considérés comme adversaires.</li>
        </ol>
      </>
    ),
    strategicNote: `C'est le bouclier numérique de l'Amérique contre l'espionnage technologique chinois (PCC). Ce décret a permis de bannir Huawei, ZTE et des dizaines de sociétés technologiques du PCC du territoire américain. Il protège les infrastructures critiques (réseaux électriques, télécoms, systèmes financiers) contre les portes dérobées installées par des adversaires étrangers. Dans le cadre du Combat, il expose le réseau de surveillance mondial que le PCC tentait de déployer via la 5G et les équipements réseau compromis.`,
  },
  {
    id: 'eo-13959',
    priorityRank: 6,
    number: '13959',
    date: '12 novembre 2020',
    signatory: 'Donald J. Trump',
    shortTitleFr: 'Menace des Investissements Finançant les Entreprises Militaires Communistes Chinoises',
    originalTitle: 'Executive Order 13959 — Addressing the Threat From Securities Investments That Finance Communist Chinese Military Companies',
    legalBasis: 'IEEPA · NEA · art. 301 titre 3 USC',
    federalRegister: '85 FR 73185',
    status: 'En vigueur (modifié par EO 13974)',
    badgeLabel: 'GUERRE FINANCIÈRE · CHINE',
    badgeClass: 'eo-badge--red',
    icon: Building2,
    resume: (
      <>
        <p>Le Président déclare l'urgence nationale face à l'exploitation par la République Populaire de Chine (RPC) des capitaux américains pour financer la modernisation de son appareil militaire, de renseignement et de sécurité.</p>
        <p className="mt-3"><strong className="text-amber-300">Constatations clés :</strong></p>
        <ul>
          <li>La RPC exploite les marchés financiers américains pour financer ses armes de destruction massive, ses armes conventionnelles avancées et ses cyber-opérations</li>
          <li>La stratégie de « Fusion Militaire-Civile » oblige les entreprises civiles chinoises à soutenir l'appareil militaire</li>
          <li>Ces entreprises lèvent des capitaux en vendant des titres aux investisseurs américains</li>
        </ul>
        <p className="mt-3"><strong className="text-amber-300">Interdictions :</strong></p>
        <ol>
          <li><strong>Interdiction de transaction</strong> — À partir du 11 janvier 2021, tout citoyen ou entité américaine est interdit d'acheter ou de détenir des titres d'entreprises militaires communistes chinoises.</li>
          <li><strong>Période de désinvestissement</strong> — Les investisseurs existants disposent de 365 jours pour liquider leurs positions.</li>
          <li><strong>Liste évolutive</strong> — Le Secrétaire à la Défense et le Secrétaire au Trésor peuvent ajouter de nouvelles entreprises à la liste à tout moment.</li>
          <li><strong>Définitions</strong> — « Entreprise Militaire Communiste Chinoise » inclut toute entité listée par le Secrétaire à la Défense comme opérant directement ou indirectement aux États-Unis.</li>
        </ol>
      </>
    ),
    strategicNote: `Ce décret coupe les vivres financiers au complexe militaro-industriel du PCC. Wall Street et les fonds d'investissement américains finançaient directement les missiles, les drones et les systèmes de surveillance utilisés CONTRE les États-Unis et leurs alliés. En interdisant ces investissements, Trump assèche le financement de la machine de guerre chinoise par ses propres citoyens. C'est l'arme de la guerre financière contre l'ancien régime chinois et ses complices à Wall Street.`,
  },
  {
    id: 'eo-14395',
    priorityRank: 7,
    number: '14395',
    date: '16 mars 2026',
    signatory: 'Donald J. Trump (Second Mandat)',
    shortTitleFr: "Création de la Task Force pour l'Élimination de la Fraude",
    originalTitle: 'Executive Order 14395 — Establishing the Task Force To Eliminate Fraud',
    legalBasis: 'Constitution · Federal Financial Management Improvement Act · False Claims Act',
    federalRegister: 'Federal Register 2026 (EO 14395)',
    status: 'En vigueur — Second mandat Trump',
    badgeLabel: 'TASK FORCE ANTI-FRAUDE · TRUMP 2.0',
    badgeClass: 'eo-badge--amber',
    icon: Crosshair,
    resume: (
      <>
        <p>Le Président Trump, lors de son second mandat, crée une Task Force présidentielle pour éliminer la fraude massive dans les programmes fédéraux.</p>
        <p className="mt-3"><strong className="text-amber-300">Constatations explosives :</strong></p>
        <ul>
          <li>Des États ont volontairement contourné les contrôles d'éligibilité pour maximiser les dépenses fédérales</li>
          <li>Des aliens illégaux, criminels, gangs étrangers et organisations non-gouvernementales exploitent les programmes sociaux</li>
          <li>Des acteurs politiques corrompus utilisent les programmes d'aide sociale pour consolider leur pouvoir politique</li>
          <li>Des officiels publics admettent des populations migrantes dans le pays pour augmenter leur base électorale</li>
          <li>L'absence de mesures d'intégrité électorale permet à certains migrants inéligibles de voter</li>
        </ul>
        <p className="mt-3"><strong className="text-amber-300">Exemple documenté (Minnesota) :</strong></p>
        <ul>
          <li>Fraude Medicaid estimée en milliards</li>
          <li>9% des 866 millions $ de food stamps dépensés en erreur</li>
          <li>L'association « Feeding our Future » a volé 250 millions $ destinés aux enfants</li>
          <li>Un réseau organisé d'immigrants somaliens a détourné des centaines de millions en garde d'enfants</li>
          <li>Des fonds auraient été acheminés vers un groupe terroriste africain</li>
        </ul>
        <p className="mt-3"><strong className="text-amber-300">Structure de la Task Force :</strong></p>
        <ul>
          <li><strong>Président :</strong> Le Vice-Président JD Vance</li>
          <li><strong>Vice-Président :</strong> Président de la FTC</li>
          <li><strong>Membres :</strong> Trésor, Justice, Agriculture, Travail, Santé, Logement, Éducation, Anciens Combattants, Sécurité Intérieure, SBA, OMB</li>
        </ul>
        <p className="mt-3"><strong className="text-amber-300">Pouvoirs :</strong></p>
        <ol>
          <li>Vérification d'éligibilité renforcée pour tous les programmes fédéraux</li>
          <li>Contrôles préventifs AVANT le versement des fonds</li>
          <li>Partage obligatoire de données entre gouvernements fédéral, étatiques et locaux</li>
          <li>Démantèlement des réseaux de fraude et de leurs facilitateurs</li>
          <li>Blocage des transferts de fonds provenant de la fraude</li>
          <li>Sanctions contre les juridictions refusant les contrôles anti-fraude (retrait de fonds fédéraux)</li>
          <li>Encouragement des poursuites civiles par des citoyens privés (Whistleblowers)</li>
        </ol>
      </>
    ),
    strategicNote: `C'est l'arme finale du second mandat — la Task Force qui démantèle le système de corruption institutionnalisée par lequel les élites politiques détournent l'argent des contribuables américains pour financer leur machine politique. Ce décret nomme explicitement les mécanismes de fraude électorale liés à l'immigration illégale et à la manipulation des programmes sociaux. Dirigée par JD Vance, cette Task Force est l'instrument de la PURGE FINANCIÈRE du système, avec le pouvoir de couper les fonds aux États complices et de poursuivre en justice les fonctionnaires corrompus. C'est le Combat en action directe.`,
  },
];

function EOCard({ eo, index }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = eo.icon;
  const isPriority = eo.priorityRank <= 2;
  const priorityFlagClass = eo.priorityRank === 1 ? 'eo-priority-flag' : 'eo-priority-flag eo-priority-flag--red';

  return (
    <motion.article
      data-testid={`eo-card-${eo.number}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`eo-doc-card ${isPriority ? 'eo-doc-card--priority' : ''}`}
    >
      {isPriority && (
        <span className={priorityFlagClass} data-testid={`eo-priority-flag-${eo.number}`}>
          <Flame className="h-3 w-3" /> PRIORITÉ #{eo.priorityRank}
        </span>
      )}

      <div className="eo-classified-strip" />

      <header className="eo-doc-card__header">
        <span className="eo-corner-mark tl" />
        <span className="eo-corner-mark tr" />

        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className={`mt-1 h-11 w-11 rounded border flex items-center justify-center shrink-0 ${
              isPriority
                ? 'border-amber-400/70 bg-amber-500/15'
                : 'border-amber-400/50 bg-amber-500/10'
            }`}>
              <Icon className="h-5 w-5 text-amber-300" />
            </div>
            <div className="min-w-0">
              <div className="mono text-[10px] tracking-[0.25em] text-amber-300/80 uppercase mb-1">
                {eo.priorityRank <= 2
                  ? <>PRIORITÉ #{eo.priorityRank} · </>
                  : <>N° {eo.priorityRank} · </>}
                Executive Order N° {eo.number} · {eo.date}
              </div>
              <h3 className={`serif text-1 leading-tight mb-1.5 ${
                isPriority ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'
              }`}>
                {eo.shortTitleFr}
              </h3>
              <div className="mono text-[11px] text-3 italic">
                {eo.originalTitle}
              </div>
            </div>
          </div>

          <span className={`eo-badge ${eo.badgeClass} shrink-0`}>
            <Stamp className="h-3 w-3" />
            {eo.badgeLabel}
          </span>
        </div>
      </header>

      <div className="eo-doc-card__body">
        <div className="eo-meta-block">
          <div className="eo-meta-item">
            <div className="label">Date de signature</div>
            <div className="value">{eo.date}</div>
          </div>
          <div className="eo-meta-item">
            <div className="label">Statut</div>
            <div className="value text-emerald-300">{eo.status}</div>
          </div>
          <div className="eo-meta-item">
            <div className="label">Federal Register</div>
            <div className="value">{eo.federalRegister}</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="mono text-[10px] tracking-[0.2em] text-amber-300/80 uppercase mb-2">Résumé</div>
          <div className="text-2 leading-relaxed text-[15px]" data-testid={`eo-resume-${eo.number}`}>
            {eo.resume}
          </div>
        </div>

        {/* NOTE STRATÉGIQUE — Callout */}
        <div className="strategic-note" data-testid={`eo-strategic-${eo.number}`}>
          <div className="strategic-note__header">
            <Flame className="h-3.5 w-3.5" />
            Note Stratégique — Pourquoi cet Ordre est une Arme du Combat
          </div>
          <div className="strategic-note__body">
            {eo.strategicNote}
          </div>
        </div>

        <div className="mono text-[10px] text-3 mt-5">
          <span className="text-4">Base juridique : </span>
          <span className="text-2">{eo.legalBasis}</span>
          <span className="text-4"> · Signataire : </span>
          <span className="text-2">{eo.signatory}</span>
        </div>

        <button
          data-testid={`eo-toggle-${eo.number}`}
          onClick={() => setExpanded(v => !v)}
          className="mt-4 btn-ghost px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 border-amber-400/40 text-amber-200 hover:border-amber-400/70"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? 'Masquer les métadonnées étendues' : 'Voir la fiche archive complète'}
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.35 }}
            className="mt-5"
          >
            <div className="mono text-[10px] tracking-[0.25em] text-amber-300/80 uppercase mb-3">
              Fiche Archive · Métadonnées étendues
            </div>
            <div className="eo-doc-card__fulltext" data-testid={`eo-fulltext-${eo.number}`}>
              <p><strong className="text-amber-300">Titre original :</strong> {eo.originalTitle}</p>
              <p><strong className="text-amber-300">Numéro EO :</strong> {eo.number}</p>
              <p><strong className="text-amber-300">Date de signature :</strong> {eo.date}</p>
              <p><strong className="text-amber-300">Signataire :</strong> {eo.signatory}</p>
              <p><strong className="text-amber-300">Base juridique :</strong> {eo.legalBasis}</p>
              <p><strong className="text-amber-300">Publication :</strong> {eo.federalRegister}</p>
              <p><strong className="text-amber-300">Statut actuel :</strong> {eo.status}</p>
            </div>
            <div className="mt-3 mono text-[10px] text-4 italic">
              Fiche officieuse · Compilation à partir du Federal Register et des archives présidentielles.
            </div>
          </motion.div>
        )}
      </div>

      <div className="px-6 pb-4 flex items-center justify-between text-[11px] mono text-4">
        <span>ARCHIVES SOUVERAINES · DOSSIER N° {eo.number}</span>
        <a
          href={`https://www.federalregister.gov/executive-order/${eo.number}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-amber-300/80 hover:text-amber-200"
        >
          Source officielle <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </motion.article>
  );
}

export default function ExecutiveOrdersPage() {
  return (
    <div className="min-h-screen bg-field" data-testid="page-ordres-executifs">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-amber-500/10">
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at 50% 40%, rgba(251,191,36,0.18) 0%, transparent 60%)'
          }}
        />
        <div className="eo-classified-strip" />

        <div className="mx-auto max-w-[1400px] px-6 py-16 relative">
          <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.25em] text-amber-300/90 uppercase mb-6 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/5">
            <ScrollText className="h-3 w-3" /> Archives Souveraines · Entité III · Ordres Exécutifs
          </div>

          <h1 className="serif font-medium leading-[1] tracking-tight text-1 mb-4">
            <span className="block text-4xl md:text-5xl lg:text-6xl">
              Ordres <span className="sovereign-title">Exécutifs</span>
            </span>
            <span className="block text-xl md:text-2xl lg:text-3xl text-amber-200 font-light mt-3 italic">
              — Armes du Combat
            </span>
          </h1>

          <p className="text-2 max-w-3xl text-base leading-relaxed mt-4">
            Compilation stratégique des 7 ordres exécutifs présidentiels constituant l'arsenal juridique
            complet contre l'ingérence étrangère, la corruption mondiale, les monopoles pharmaceutiques,
            la dépendance médicale, l'espionnage technologique, le financement de l'ennemi et la fraude
            institutionnalisée. Chaque décret est présenté avec son résumé, sa note stratégique et sa
            référence au <em>Federal Register</em>.
          </p>

          <div className="mt-6 flex flex-wrap gap-3 mono text-[11px] text-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-400/30 bg-amber-400/5 text-amber-200">
              <Flame className="h-3 w-3" /> 7 DÉCRETS · ARMES ACTIVES
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> Accès public · Sans mot de passe
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> Traductions officieuses
            </span>
          </div>
        </div>
      </section>

      {/* Section title */}
      <section className="mx-auto max-w-[1400px] px-6 pt-12 pb-4">
        <div className="flex items-center gap-4">
          <Landmark className="h-6 w-6 text-amber-300" />
          <h2 className="serif text-2xl md:text-3xl text-1 tracking-wide uppercase">
            Ordres Exécutifs <span className="text-amber-300">— Armes du Combat</span>
          </h2>
        </div>
        <div className="mt-2 mono text-[10px] tracking-[0.3em] text-3 uppercase">
          Priorité stratégique décroissante · N° 1 à 7
        </div>
      </section>

      {/* EO LIST */}
      <section className="mx-auto max-w-[1400px] px-6 pt-4 pb-12">
        <div className="grid grid-cols-1 gap-8" data-testid="eo-list">
          {EXECUTIVE_ORDERS.map((eo, i) => (
            <EOCard key={eo.id} eo={eo} index={i} />
          ))}
        </div>
      </section>

      {/* CONCLUSION */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16">
        <div className="relative eo-doc-card p-1">
          <div className="eo-classified-strip" />
          <div className="p-6 md:p-8 relative">
            <span className="eo-corner-mark tl" />
            <span className="eo-corner-mark tr" />
            <span className="eo-corner-mark bl" />
            <span className="eo-corner-mark br" />
            <div className="mono text-[10px] tracking-[0.28em] text-amber-300 uppercase mb-3 inline-flex items-center gap-2">
              <Flame className="h-3 w-3" /> Conclusion — Arsenal Juridique Complet
            </div>
            <p className="serif italic text-[15px] md:text-[17px] leading-relaxed text-amber-100/90 max-w-4xl">
              Ces 7 Ordres Exécutifs constituent l'arsenal juridique complet déployé par le Président
              Trump, du premier au second mandat, pour neutraliser les menaces à la souveraineté
              américaine : ingérence électorale, corruption mondiale, monopoles pharmaceutiques,
              dépendance médicale, espionnage technologique, financement de l'ennemi, et fraude
              institutionnalisée. Chaque décret est une arme précise, activée au moment stratégique du Combat.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER note */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16">
        <div className="glass rounded-lg p-5 border border-amber-400/20">
          <div className="mono text-[10px] tracking-[0.25em] text-amber-300/80 uppercase mb-2">
            Note éditoriale
          </div>
          <p className="text-2 text-sm leading-relaxed">
            Les résumés et notes stratégiques présentés sont des synthèses éditoriales à visée
            documentaire. Pour la version faisant foi, se référer au <em>Federal Register</em> et aux
            archives officielles du gouvernement des États-Unis. Aucune modification substantielle
            n'a été apportée au contenu juridique original des décrets.
          </p>
        </div>
      </section>
    </div>
  );
}
